import type { Order } from "@/lib/projects";
import type { SortState } from "@/components/dataTable";
import {
  getColumnLabel,
  isProjectActionColumn,
  renderCellValue,
  type HeroCustomField,
} from "@/pages/projekte/columns";
import { formatHours, toHoursUsageDisplay, type HoursUsageEntry } from "@/pages/projekte/hoursUsage";
import type { BookedHoursEntry } from "@/pages/projekte/useBookedHours";

export type ProjectPrintPaperId = "a4" | "a3" | "letter";
export type ProjectPrintOrientation = "portrait" | "landscape";

export type ProjectPrintPaper = {
  id: ProjectPrintPaperId;
  label: string;
  /** Kurze Seite in mm (Hochformat-Breite). */
  widthMm: number;
  /** Lange Seite in mm (Hochformat-Höhe). */
  heightMm: number;
};

const A4_PAPER: ProjectPrintPaper = { id: "a4", label: "A4", widthMm: 210, heightMm: 297 };

export const PROJECT_PRINT_PAPERS: ProjectPrintPaper[] = [
  A4_PAPER,
  { id: "a3", label: "A3", widthMm: 297, heightMm: 420 },
  { id: "letter", label: "Letter", widthMm: 215.9, heightMm: 279.4 },
];

/** Seitenränder, identisch in Vorschau-Padding und @page. */
export const PROJECT_PRINT_MARGIN_MM = { top: 12, right: 10, bottom: 12, left: 10 };

export type ProjectPrintPageSize = {
  widthMm: number;
  heightMm: number;
};

export function projectPrintPageSize(
  paperId: ProjectPrintPaperId,
  orientation: ProjectPrintOrientation,
): ProjectPrintPageSize {
  const paper = PROJECT_PRINT_PAPERS.find((entry) => entry.id === paperId) ?? A4_PAPER;
  if (orientation === "landscape") {
    return { widthMm: paper.heightMm, heightMm: paper.widthMm };
  }
  return { widthMm: paper.widthMm, heightMm: paper.heightMm };
}

/** Aktionsspalten (Hero, Ordner, Deckblatt) gehören nicht aufs Papier. */
export function printableProjectColumns(columns: readonly string[]): string[] {
  return columns.filter((column) => !isProjectActionColumn(column));
}

export function formatProjectPrintCount(count: number): string {
  return count === 1 ? "1 Projekt" : `${count} Projekte`;
}

export function formatProjectPrintTimestamp(date: Date): string {
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

/** Entspricht der Listenreihenfolge: ohne Sortierung liefert die API die neuesten Projekte zuerst. */
export function formatProjectSortCaption(
  sort: SortState | null,
  heroFields: HeroCustomField[],
): string {
  if (!sort) return "Sortierung: Neueste zuerst";
  const label = getColumnLabel(sort.key, heroFields);
  const direction = sort.direction === "asc" ? "aufsteigend" : "absteigend";
  return `Sortierung: ${label}, ${direction}`;
}

export function formatProjectPrintCell(
  row: Order,
  column: string,
  heroFields: HeroCustomField[],
  hours?: HoursUsageEntry,
  booked?: BookedHoursEntry,
): string {
  if (column === "hours_usage") {
    const display = toHoursUsageDisplay(hours);
    if (!display) return "—";
    if (display.loading || display.actualHours == null) return "…";
    return `${display.displayPercent} % (${formatHours(display.actualHours)} / ${formatHours(display.plannedHours)})`;
  }
  if (column === "booked_hours") {
    if (!booked || booked.loading) return "…";
    if (booked.error || booked.hours == null || !Number.isFinite(booked.hours)) return "—";
    return formatHours(booked.hours);
  }
  const cell = renderCellValue(row, column, heroFields);
  const text = [cell.value, cell.secondaryStatusLabel].filter(Boolean).join(" · ");
  return text || "—";
}

export type ProjectPrintDocumentInput = {
  title: string;
  extras: string | null;
  sortCaption: string;
  printedAt: string;
  countLabel: string;
  columns: string[];
  heroFields: HeroCustomField[];
  rows: string[][];
  paperId: ProjectPrintPaperId;
  orientation: ProjectPrintOrientation;
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatMm(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

export function buildProjectListPrintDocument(input: ProjectPrintDocumentInput): string {
  const page = projectPrintPageSize(input.paperId, input.orientation);
  const width = formatMm(page.widthMm);
  const height = formatMm(page.heightMm);
  const margin = PROJECT_PRINT_MARGIN_MM;
  const labels = input.columns.map((column) => getColumnLabel(column, input.heroFields));
  const head = labels.map((label) => `<th>${escapeHtml(label)}</th>`).join("");
  const body = input.rows
    .map((row) => {
      const cells = input.columns
        .map((_, index) => `<td>${escapeHtml(row[index] ?? "—")}</td>`)
        .join("");
      return `<tr>${cells}</tr>`;
    })
    .join("");
  const extras = input.extras?.trim()
    ? `<p class="sub">${escapeHtml(input.extras.trim())}</p>`
    : "";
  const fontSize = input.columns.length > 8 ? "8pt" : "9pt";

  return `<!doctype html>
<html lang="de">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(input.title || "Projekte")}</title>
  <style>
    @page {
      size: ${width}mm ${height}mm;
      margin: ${margin.top}mm ${margin.right}mm ${margin.bottom}mm ${margin.left}mm;
    }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      color: #111;
      font-family: "Segoe UI", system-ui, sans-serif;
    }
    @media screen {
      html { background: #fff; }
      body {
        width: ${width}mm;
        min-height: ${height}mm;
        padding: ${margin.top}mm ${margin.right}mm ${margin.bottom}mm ${margin.left}mm;
        background-color: #fff;
        background-image: linear-gradient(
          to bottom,
          transparent calc(${height}mm - 0.4mm),
          #c8c8d2 calc(${height}mm - 0.4mm),
          #c8c8d2 ${height}mm
        );
        background-size: 100% ${height}mm;
        background-repeat: repeat-y;
      }
    }
    @media print {
      html, body {
        width: auto;
        min-height: 0;
        padding: 0;
        background: #fff;
        background-image: none;
      }
    }
    h1 {
      margin: 0 0 1.5mm;
      font-size: 14pt;
      line-height: 1.25;
      font-weight: 600;
    }
    .sub, .meta {
      margin: 0 0 1.5mm;
      font-size: 8.5pt;
      line-height: 1.35;
      color: #333;
    }
    table {
      width: 100%;
      margin-top: 3mm;
      border-collapse: collapse;
      font-size: ${fontSize};
      line-height: 1.3;
    }
    thead { display: table-header-group; }
    tr { break-inside: avoid; }
    th, td {
      padding: 1.1mm 1.4mm;
      border-bottom: 0.2mm solid #d5d5dc;
      text-align: left;
      vertical-align: top;
      word-break: break-word;
    }
    th {
      border-bottom: 0.45mm solid #111;
      font-size: 7.5pt;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    tbody tr:nth-child(even) td { background: #f4f4f7; }
  </style>
</head>
<body>
  <h1>${escapeHtml(input.title || "Projekte")}</h1>
  ${extras}
  <p class="meta">${escapeHtml(input.sortCaption)} · ${escapeHtml(input.countLabel)} · ${escapeHtml(input.printedAt)}</p>
  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body}</tbody>
  </table>
</body>
</html>`;
}
